import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Menu, Transition } from "@headlessui/react";
import {
  PencilSquareIcon,
  PaperAirplaneIcon,
  PrinterIcon,
  BuildingOffice2Icon,
  EllipsisHorizontalIcon,
  DocumentDuplicateIcon,
  ArrowUturnLeftIcon,
  ArrowUpTrayIcon,
  ArrowPathIcon,
  BookmarkSquareIcon,
  ClockIcon,
  TrashIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { useSettings } from "../../../contexts/settings-context";
import { TWclassNames } from "../../utils/Div";
import { dateToString } from "../../Utils";
import {
  OfferStatus,
  isOfferFeatureEnabled,
  isOfferTemplateVersionsEnabled,
  resolveOfferSalesAmount,
  shouldShowCreateRevisionMenuItem,
} from "./utils";
import {
  alternateOfferLayout,
  resolveOfferLayout,
} from "./utils/offer-layout";

const EMPTY = "—";

/**
 * Resolve a user-facing revision number from the offer payload. The offer show
 * payload only carries raw revision ids (`current_draft_revision_id`), so we
 * prefer an embedded revision object / list when the backend provides one and
 * only ever surface a real `revision_number` — never a raw DB id. Returns null
 * when no real number is known so the caller can hide the "Rev." chip.
 */
function resolveRevisionNumber(offer) {
  if (!offer) return null;

  const embedded =
    offer.current_draft_revision?.revision_number ??
    offer.accepted_revision?.revision_number;
  if (embedded != null) return embedded;

  if (Array.isArray(offer.revisions) && offer.revisions.length > 0) {
    const numbers = offer.revisions
      .map((rev) => rev?.revision_number)
      .filter((num) => num != null);
    if (numbers.length > 0) return Math.max(...numbers);
  }

  if (offer.revision_number != null) return offer.revision_number;
  if (offer.version != null) return offer.version;

  return null;
}

/**
 * Compact read-only Claude-style banner for the offer detail view.
 * Shows stamdata (title, status, number · rev · kunde · prosjekt · ansvarlig),
 * Tilbudssum + Gyldig til, primary actions and a tab row. All data is read-only;
 * editing happens in the separate edit dialog (see offer-edit-dialog.js).
 */
export default function OfferSummaryHeader({
  offer,
  featureFlags,
  isLoading = false,
  tabs = [],
  activeTab,
  onTabChange,
  onEdit,
  /** Internal submit-for-review («Send til kontroll») */
  onSubmitForReview,
  /** Customer send («Send tilbud») — also accepts legacy `onSend` */
  onSendOffer,
  onSend,
  onPdf,
  onConvert,
  convertingToProject = false,
  pdfLoading = false,
  onCopy,
  onDelete,
  onHelp,
  /** «Lagre som nytt tilbud» — opens OfferSaveAsDialog */
  onSaveAs,
  /** «Overskriv eksisterende tilbud» — opens OfferOverwriteDialog */
  onOverwrite,
  /** «Lagre som tilbudsmal» — opens OfferSaveAsTemplateDialog */
  onSaveAsTemplate,
  /** «Oppdater tilbudsmal…» — opens OfferSaveToTemplateDialog */
  onSaveToTemplate,
  /** «Versjonshistorikk (mal)» — only relevant when offer.is_template */
  onViewTemplateVersions,
  /** «Opprett ny revisjon» — opens OfferCreateRevisionDialog */
  onCreateRevision,
  notesSlot = null,
}) {
  const handleSendOffer = onSendOffer || onSend;
  const { t } = useTranslation();
  const { priceFormatter } = useSettings();

  const flags = featureFlags || offer?.offer_feature_flags || {};
  const revisionsEnabled = isOfferFeatureEnabled(flags, "offer_revisions");
  const conversionEnabled = isOfferFeatureEnabled(
    flags,
    "offer_project_conversion_v2"
  );
  const templateVersionsEnabled = isOfferTemplateVersionsEnabled(flags);
  const showTemplateVersions = Boolean(
    offer?.is_template && templateVersionsEnabled && onViewTemplateVersions
  );
  const showCreateRevision = Boolean(
    typeof onCreateRevision === "function" &&
      shouldShowCreateRevisionMenuItem(offer, flags)
  );
  const offerIsAccepted =
    String(offer?.status || "").toLowerCase() === "accepted" ||
    Boolean(offer?.accepted_revision_id);
  const showConvert = conversionEnabled && offerIsAccepted;

  // Prefer human title (Claude IA); API displayTitle is often "Tilbud {nr}".
  const displayTitle =
    offer?.title ||
    offer?.name ||
    offer?.displayTitle ||
    (offer?.id ? `${t("offers.banner.offer")} #${offer?.id}` : t("offers.banner.new"));

  const revisionNumber = resolveRevisionNumber(offer);
  const revisionLabel =
    revisionsEnabled && revisionNumber != null
      ? `${t("offers.banner.revision")} ${revisionNumber}`
      : null;

  const metaSegments = [
    offer?.offer_number,
    revisionLabel,
    offer?.customer?.name || offer?.customer?.displayTitle,
    offer?.project?.name || offer?.project?.displayTitle
      ? `${t("offers.banner.project")}: ${
          offer?.project?.name || offer?.project?.displayTitle
        }`
      : null,
    offer?.our_contact_employee?.name || offer?.our_contact_employee?.displayTitle
      ? `${t("offers.banner.responsible")}: ${
          offer?.our_contact_employee?.name ||
          offer?.our_contact_employee?.displayTitle
        }`
      : null,
  ].filter(Boolean);

  const tilbudssum = priceFormatter?.format
    ? priceFormatter.format(resolveOfferSalesAmount(offer))
    : resolveOfferSalesAmount(offer);
  const gyldigTil = offer?.valid_to ? dateToString(offer.valid_to) : EMPTY;

  const disabled = isLoading || !offer?.id;
  const printLayout = resolveOfferLayout(offer);
  const otherPrintLayout = alternateOfferLayout(offer);

  return (
    <div className="border-b border-slate-200 bg-white">
      {/* Full-bleed banner (Claude IA) — no max-w-7xl so chrome spans the page */}
      <div className="w-full px-4 pt-4 sm:px-5 lg:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          {/* Left: title + stamdata */}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-[17px] font-semibold text-slate-900 sm:text-lg">
                {displayTitle}
              </h1>
              <OfferStatus offer={offer} />
            </div>
            {metaSegments.length > 0 && (
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                {metaSegments.map((segment, index) => (
                  <span
                    key={index}
                    className="flex items-center gap-3 whitespace-nowrap"
                  >
                    {index > 0 && (
                      <span className="text-slate-300" aria-hidden="true">
                        ·
                      </span>
                    )}
                    <span className="min-w-0 truncate">{segment}</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Right: amounts + Claude action cluster (secondary + primary + overflow) */}
          <div className="flex flex-wrap items-center gap-4 lg:justify-end">
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">
                {t("offers.banner.total")}
              </div>
              <div className="text-base font-semibold text-slate-900">
                {tilbudssum}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">
                {t("offers.banner.valid_to")}
              </div>
              <div className="text-sm font-medium text-slate-900">
                {gyldigTil}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {notesSlot}
              {onHelp ? (
                <button
                  type="button"
                  onClick={onHelp}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
                  title={t("offers.help.open")}
                  aria-label={t("offers.help.open")}
                >
                  <QuestionMarkCircleIcon className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">{t("offers.help.open")}</span>
                </button>
              ) : null}
              <button
                type="button"
                onClick={onEdit}
                disabled={disabled}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                {t("offers.banner.edit")}
              </button>
              {typeof onPdf === "function" ? (
                <button
                  type="button"
                  onClick={() => onPdf(printLayout)}
                  disabled={disabled || pdfLoading}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PrinterIcon className="h-4 w-4" aria-hidden="true" />
                  {pdfLoading
                    ? t("Loading...")
                    : t("offers.banner.print_current", {
                        layout: t(`offers.workspace.layout_${printLayout}`),
                      })}
                </button>
              ) : null}
              {revisionsEnabled && typeof onSubmitForReview === "function" ? (
                <button
                  type="button"
                  onClick={onSubmitForReview}
                  disabled={disabled}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-transparent bg-[#1463C7] px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PaperAirplaneIcon className="h-4 w-4" aria-hidden="true" />
                  {t("offers.banner.send")}
                </button>
              ) : typeof handleSendOffer === "function" ? (
                <button
                  type="button"
                  onClick={handleSendOffer}
                  disabled={disabled}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-transparent bg-[#1463C7] px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PaperAirplaneIcon className="h-4 w-4" aria-hidden="true" />
                  {t("offers.banner.send_offer")}
                </button>
              ) : null}

              <Menu as="div" className="relative">
                <Menu.Button
                  className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-2 py-2 text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
                  aria-label={t("offers.banner.more")}
                >
                  <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-150"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-100"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-50 mt-2 w-64 origin-top-right rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {revisionsEnabled && typeof handleSendOffer === "function" && (
                      <Menu.Item disabled={disabled}>
                        {({ active, disabled: itemDisabled }) => (
                          <button
                            type="button"
                            onClick={itemDisabled ? undefined : handleSendOffer}
                            disabled={itemDisabled}
                            className={TWclassNames(
                              active && !itemDisabled ? "bg-slate-50" : "",
                              itemDisabled
                                ? "cursor-not-allowed opacity-50"
                                : "text-slate-700",
                              "flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm"
                            )}
                          >
                            <PaperAirplaneIcon
                              className="h-4 w-4 text-slate-400"
                              aria-hidden="true"
                            />
                            {t("offers.menu.send_offer")}
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    <Menu.Item disabled={disabled || pdfLoading}>
                      {({ active, disabled: itemDisabled }) => (
                        <button
                          type="button"
                          onClick={itemDisabled ? undefined : () => onPdf?.(printLayout)}
                          disabled={itemDisabled}
                          className={TWclassNames(
                            active && !itemDisabled ? "bg-slate-50" : "",
                            itemDisabled
                              ? "cursor-not-allowed opacity-50"
                              : "text-slate-700",
                            "flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm"
                          )}
                        >
                          <PrinterIcon
                            className="h-4 w-4 text-slate-400"
                            aria-hidden="true"
                          />
                          {pdfLoading
                            ? t("Loading...")
                            : t("offers.banner.print_current", {
                                layout: t(`offers.workspace.layout_${printLayout}`),
                              })}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item disabled={disabled || pdfLoading}>
                      {({ active, disabled: itemDisabled }) => (
                        <button
                          type="button"
                          onClick={
                            itemDisabled
                              ? undefined
                              : () => onPdf?.(otherPrintLayout)
                          }
                          disabled={itemDisabled}
                          className={TWclassNames(
                            active && !itemDisabled ? "bg-slate-50" : "",
                            itemDisabled
                              ? "cursor-not-allowed opacity-50"
                              : "text-slate-700",
                            "flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm"
                          )}
                        >
                          <PrinterIcon
                            className="h-4 w-4 text-slate-400"
                            aria-hidden="true"
                          />
                          {pdfLoading
                            ? t("Loading...")
                            : t("offers.banner.print_other", {
                                layout: t(
                                  `offers.workspace.layout_${otherPrintLayout}`
                                ),
                              })}
                        </button>
                      )}
                    </Menu.Item>
                    {showConvert && (
                      <Menu.Item disabled={disabled || convertingToProject}>
                        {({ active, disabled: itemDisabled }) => (
                          <button
                            type="button"
                            onClick={itemDisabled ? undefined : onConvert}
                            disabled={itemDisabled}
                            className={TWclassNames(
                              active && !itemDisabled ? "bg-slate-50" : "",
                              itemDisabled
                                ? "cursor-not-allowed opacity-50"
                                : "text-slate-700",
                              "flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm"
                            )}
                          >
                            <BuildingOffice2Icon
                              className="h-4 w-4 text-slate-400"
                              aria-hidden="true"
                            />
                            {offer?.project_id
                              ? t("offers.convert.open_project")
                              : convertingToProject
                                ? t("offers.convert.converting")
                                : t("offers.banner.convert")}
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          type="button"
                          onClick={onCopy}
                          className={TWclassNames(
                            active ? "bg-slate-50" : "",
                            "flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-700"
                          )}
                        >
                          <DocumentDuplicateIcon
                            className="h-4 w-4 text-slate-400"
                            aria-hidden="true"
                          />
                          {t("offers.banner.copy")}
                        </button>
                      )}
                    </Menu.Item>
                    {showCreateRevision && (
                      <Menu.Item disabled={disabled}>
                        {({ active, disabled: itemDisabled }) => (
                          <button
                            type="button"
                            onClick={itemDisabled ? undefined : onCreateRevision}
                            disabled={itemDisabled}
                            className={TWclassNames(
                              active && !itemDisabled ? "bg-slate-50" : "",
                              itemDisabled
                                ? "cursor-not-allowed opacity-50"
                                : "text-slate-700",
                              "flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm"
                            )}
                          >
                            <ArrowPathIcon
                              className="h-4 w-4 text-slate-400"
                              aria-hidden="true"
                            />
                            {t("offers.menu.create_revision")}
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    {typeof onSaveAs === "function" && (
                      <Menu.Item disabled={disabled}>
                        {({ active, disabled: itemDisabled }) => (
                          <button
                            type="button"
                            onClick={itemDisabled ? undefined : onSaveAs}
                            disabled={itemDisabled}
                            className={TWclassNames(
                              active && !itemDisabled ? "bg-slate-50" : "",
                              itemDisabled ? "cursor-not-allowed opacity-50" : "text-slate-700",
                              "flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm"
                            )}
                          >
                            <BookmarkSquareIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                            {t("offers.menu.save_as")}
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    {typeof onOverwrite === "function" && (
                      <Menu.Item disabled={disabled}>
                        {({ active, disabled: itemDisabled }) => (
                          <button
                            type="button"
                            onClick={itemDisabled ? undefined : onOverwrite}
                            disabled={itemDisabled}
                            className={TWclassNames(
                              active && !itemDisabled ? "bg-slate-50" : "",
                              itemDisabled ? "cursor-not-allowed opacity-50" : "text-slate-700",
                              "flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm"
                            )}
                          >
                            <ArrowUturnLeftIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                            {t("offers.menu.overwrite")}
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    {typeof onSaveAsTemplate === "function" && (
                      <Menu.Item disabled={disabled}>
                        {({ active, disabled: itemDisabled }) => (
                          <button
                            type="button"
                            onClick={itemDisabled ? undefined : onSaveAsTemplate}
                            disabled={itemDisabled}
                            className={TWclassNames(
                              active && !itemDisabled ? "bg-slate-50" : "",
                              itemDisabled ? "cursor-not-allowed opacity-50" : "text-slate-700",
                              "flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm"
                            )}
                          >
                            <ArrowUpTrayIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                            {t("offers.menu.save_as_template")}
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    {typeof onSaveToTemplate === "function" && (
                      <Menu.Item disabled={disabled}>
                        {({ active, disabled: itemDisabled }) => (
                          <button
                            type="button"
                            onClick={itemDisabled ? undefined : onSaveToTemplate}
                            disabled={itemDisabled}
                            className={TWclassNames(
                              active && !itemDisabled ? "bg-slate-50" : "",
                              itemDisabled ? "cursor-not-allowed opacity-50" : "text-slate-700",
                              "flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm"
                            )}
                          >
                            <ArrowUpTrayIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                            {t("offers.menu.save_to_template")}
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    {showTemplateVersions && (
                      <Menu.Item disabled={disabled}>
                        {({ active, disabled: itemDisabled }) => (
                          <button
                            type="button"
                            onClick={itemDisabled ? undefined : onViewTemplateVersions}
                            disabled={itemDisabled}
                            className={TWclassNames(
                              active && !itemDisabled ? "bg-slate-50" : "",
                              itemDisabled ? "cursor-not-allowed opacity-50" : "text-slate-700",
                              "flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm"
                            )}
                          >
                            <ClockIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                            {t("offers.menu.template_versions")}
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    <Menu.Item disabled={Boolean(offer?.invoice?.id)}>
                      {({ active, disabled: itemDisabled }) => (
                        <button
                          type="button"
                          onClick={itemDisabled ? undefined : onDelete}
                          disabled={itemDisabled}
                          className={TWclassNames(
                            active && !itemDisabled ? "bg-red-50" : "",
                            itemDisabled
                              ? "cursor-not-allowed opacity-50"
                              : "text-red-600",
                            "flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm"
                          )}
                        >
                          <TrashIcon className="h-4 w-4" aria-hidden="true" />
                          {t("offers.banner.delete")}
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>

        {/* Flat primary tabs — no numbered 01–04 area chrome */}
        {tabs.length > 0 && (
          <div
            className="mt-4 flex gap-1 overflow-x-auto"
            role="tablist"
            aria-label={t("offers.banner.tabs")}
          >
            {tabs.map((tab) => {
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  disabled={tab.disabled}
                  title={tab.disabled ? tab.disabledHint : undefined}
                  onClick={() => !tab.disabled && onTabChange?.(tab.id)}
                  className={TWclassNames(
                    "whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
                    tab.disabled
                      ? "cursor-not-allowed border-transparent text-slate-300"
                      : isActive
                        ? "border-[#1463C7] text-[#1463C7]"
                        : "border-transparent text-slate-500 hover:text-slate-800"
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
